import { lazy, Suspense } from 'react';
import { Route, BrowserRouter as Router, Routes } from 'react-router';
import FullViewLoading from 'components/common/FullViewLoading';
import config from 'config/index';
import MainLayout from 'pages/layouts/MainLayout';

import { ThemesOutlet } from '@/themes';
import Login from 'pages/authorization/Login';
import ScoreInterviewPage, { EScoringBuildSystem } from 'pages/score/ScoreInterviewPage';

// Dashboard
const AdminDashboard = lazy(() => import('pages/dashboard/AdminDashboard'));

// Applications
const SilencePublishErrors = lazy(() => import('pages/applications/SilencePublishErrors'));

// Clients
const ClientsIndex = lazy(() => import('pages/clients'));
const ClientPage = lazy(() => import('pages/clients/ClientPage'));
const ClientOutreachCampaigns = lazy(() => import('pages/outreach/ClientOutreachCampaigns'));
const CampaignDetails = lazy(() => import('pages/outreach/CampaignDetails'));
const CampaignEditor = lazy(() => import('../pages/outreach/CampaignEditor'));
const AllCampaigns = lazy(() => import('pages/outreach/AllCampaigns'));
const AllTemplates = lazy(() => import('pages/outreach/AllTemplates'));
const TemplateEditor = lazy(() => import('pages/outreach/TemplateEditor'));
const ClientMediaUpload = lazy(() => import('pages/clients/ClientMediaUpload'));

// // Candidates
const CandidatesIndex = lazy(() => import('pages/candidates'));
const CandidatePage = lazy(() => import('pages/candidates/CandidatePage'));

// User
const UserRoleBasedComponent = lazy(() => import('pages/user/index'));

// Jobs
const Job = lazy(() => import('components/Jobs/Job'));
const FindJob = lazy(() => import('components/Jobs/FindJob'));

// Templates
const TemplatesIndex = lazy(() => import('pages/templates/TemplatesIndex'));
const TemplatePage = lazy(() => import('pages/templates/TemplatePage'));
const TemplateRevisions = lazy(() => import('pages/templates/TemplateRevisions'));

// // Questions
const QuestionsIndex = lazy(() => import('pages/questions/QuestionsIndex'));
const QuestionPage = lazy(() => import('pages/questions/QuestionPage'));

// Interviews
const InterviewsIndex = lazy(() => import('pages/interviews/InterviewsIndex'));

// Score
const ScoreInterviewIndex = lazy(() => import('pages/score/ScoreInterviewIndex'));

// Theme
const ThemesPage = lazy(() => import('@/themes').then((m) => ({ default: m.ThemesPage })));
const ThemeEditorPage = lazy(() => import('@/themes').then((m) => ({ default: m.ThemeEditorPage })));
const ThemesIndexPage = lazy(() => import('@/themes').then((m) => ({ default: m.ThemesIndexPage })));

const AgentChatIndex = lazy(() => import('pages/agent-chat/AgentChatIndex'));

import { VersionCheck } from '@/VersionCheck';
import {
  AgentChatOutlet,
  CandidatesOutlet,
  ClientsOutlet,
  Forbidden403,
  InterviewsOutlet,
  JobsOutlet,
  NoMatch,
  QuestionsOutlet,
  ScoresOutlet,
  TemplatesOutlet,
  Unauthorized401,
} from 'pages/outlets/Outlets';
import { OutreachCandidate } from 'pages/outreach/OutreachCandidate';
import { ETemplateRevision } from 'rtk/TemplateSlice';

const App = () => {
  if (!import.meta.env.VITE_BACKEND_URL || !config.VITE_BACKEND_URL) {
    throw new Error(`Backend URL is not defined: ${config.VITE_BACKEND_URL}`);
  }

  return (
    <Router>
      <VersionCheck />
      <Routes>
        <Route path="/" element={<MainLayout />}>
          <Route index element={<AdminDashboard />} />

          <Route path="clients" element={<ClientsOutlet />}>
            <Route index element={<ClientsIndex />} />
            <Route path=":clientId" element={<ClientPage />} />
            <Route path=":clientId/media" element={<ClientMediaUpload />} />
            <Route path=":clientId/outreach" element={<ClientOutreachCampaigns />} />
          </Route>

          <Route path="outreach/create" element={<CampaignEditor />} />
          <Route path="outreach/campaigns" element={<AllCampaigns />} />
          <Route path="outreach/campaigns/:campaignId" element={<CampaignDetails />} />
          <Route path="outreach/campaigns/:campaignId/edit" element={<CampaignEditor />} />
          <Route path="outreach/templates" element={<AllTemplates />} />
          <Route path="outreach/templates/create" element={<TemplateEditor />} />
          <Route path="outreach/templates/:templateId/edit" element={<TemplateEditor />} />

          <Route path="outreachCandidates/:outreachCandidateId" element={<OutreachCandidate />}></Route>

          <Route path="applications/:applicationId/silence-publish-errors" element={<SilencePublishErrors />}></Route>

          <Route path="candidates" element={<CandidatesOutlet />}>
            <Route index element={<CandidatesIndex />} />
            <Route path=":candidateId" element={<CandidatePage />} />
          </Route>

          <Route path="jobs" element={<JobsOutlet />}>
            <Route index element={<FindJob />} />
            <Route path=":jobId" element={<Job />} />
          </Route>

          <Route path="score" element={<ScoresOutlet />}>
            <Route index element={<ScoreInterviewIndex />} />
            <Route path=":interviewId" element={<ScoreInterviewPage />} />
            <Route
              path=":interviewId/old-build"
              element={<ScoreInterviewPage scoringBuildSystem={EScoringBuildSystem.Old} />}
            />
            <Route
              path=":interviewId/new-build"
              element={<ScoreInterviewPage scoringBuildSystem={EScoringBuildSystem.New} />}
            />
          </Route>

          <Route path="templates" element={<TemplatesOutlet />}>
            <Route index element={<TemplatesIndex />} />
            <Route path=":templateId">
              <Route index element={<TemplatePage revision={ETemplateRevision.Current} />} />
              <Route path="current" element={<TemplatePage revision={ETemplateRevision.Current} />} />
              <Route path="draft" element={<TemplatePage revision={ETemplateRevision.Draft} />} />
              <Route path="revisions" element={<TemplateRevisions />} />
              <Route path=":processId" element={<TemplatePage revision={ETemplateRevision.Process} />} />
            </Route>
          </Route>

          <Route path="questions" element={<QuestionsOutlet />}>
            <Route index element={<QuestionsIndex />} />
            <Route path=":questionId" element={<QuestionPage />} />
          </Route>

          <Route path="interviews" element={<InterviewsOutlet />}>
            <Route index element={<InterviewsIndex />} />
          </Route>

          <Route
            path="settings"
            element={
              <Suspense fallback={<FullViewLoading title="Loading settings page." />}>
                <UserRoleBasedComponent />
              </Suspense>
            }
          />

          <Route path="themes" element={<ThemesOutlet />}>
            <Route element={<ThemesPage />}>
              <Route index element={<ThemesIndexPage />} />
              <Route path=":themeId" element={<ThemeEditorPage />} />
            </Route>
          </Route>

          <Route path="agent-chat" element={<AgentChatOutlet />}>
            <Route index element={<AgentChatIndex />} />
            <Route path="/agent-chat/:sessionId" element={<AgentChatIndex />} />
          </Route>

          <Route path="*" element={<NoMatch />} />
        </Route>

        <Route path="login" element={<Login />} />

        <Route path="401" element={<Unauthorized401 />} />
        <Route path="403" element={<Forbidden403 />} />
      </Routes>
    </Router>
  );
};

export default App;
